import { useId, useState, type FormEvent } from "react";
import { Copy, Download, ExternalLink } from "lucide-react";
import { parseAddresses, type EmailDraft, type EmailInput } from "@/api/emails";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { Button, buttonClass } from "@/components/ui/button";
import { TextAreaField, TextField } from "@/components/ui/field";
import { gmailComposeUrl, mailtoUrl } from "@/lib/mail";
import { useFormValues, useSubmitState } from "@/lib/use-form";

type EmailFormProps = {
  draft: EmailDraft;
  /** The company mailbox the directors send invoices from by hand. */
  mailbox: string;
  alreadySent: boolean;
  downloading: boolean;
  onDownload: () => void;
  /** Send through the app's mail server (only when the server has one). */
  onSend: (input: EmailInput) => Promise<unknown>;
  /** Record that the invoice was e-mailed by hand. */
  onMarkEmailed: (to: string[]) => Promise<unknown>;
  onCancel: () => void;
  onReload?: () => void;
};

/**
 * The invoice e-mail, ready to edit. Without a mail server on the app it is sent by hand:
 * download the PDF, open the message in Gmail or the mail app, attach and send, then mark
 * the invoice as e-mailed.
 */
export function EmailForm({
  draft,
  mailbox,
  alreadySent,
  downloading,
  onDownload,
  onSend,
  onMarkEmailed,
  onCancel,
  onReload,
}: EmailFormProps) {
  const id = useId();
  const [manual, setManual] = useState(!draft.email_configured);
  const [copied, setCopied] = useState(false);
  const [missingRecipient, setMissingRecipient] = useState(false);
  const { values, set } = useFormValues({
    to: draft.to.join(", "),
    cc: draft.cc.join(", "),
    subject: draft.subject,
    body: draft.body,
  });
  const { error, errors, saving, run } = useSubmitState();
  const message = {
    to: parseAddresses(values.to),
    cc: parseAddresses(values.cc),
    subject: values.subject.trim(),
    body: values.body,
  };

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (manual) {
      void run(() => onMarkEmailed(message.to));
      return;
    }
    setMissingRecipient(message.to.length === 0);
    if (message.to.length === 0) return;
    void run(() => onSend({ ...message, document_version: draft.document_version }));
  }

  async function copyText(): Promise<void> {
    try {
      await navigator.clipboard.writeText(`${message.subject}\n\n${message.body}`);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      {manual ? (
        <ol className="list-decimal space-y-1 pl-5 text-sm">
          <li>Download the PDF{draft.attachment_name ? ` (${draft.attachment_name})` : ""}.</li>
          <li>
            Open the message in Gmail as <strong>{mailbox}</strong> (or your mail app), attach the
            PDF and send it.
          </li>
          <li>Mark the invoice as e-mailed here.</li>
        </ol>
      ) : (
        <p className="break-words text-sm text-muted-foreground">
          From {draft.from_address ?? "—"} · Attachment {draft.attachment_name ?? "—"}
        </p>
      )}
      <RequestError error={error} onReload={onReload} />
      <TextField
        id={`${id}-to`}
        label="To"
        type="email"
        multiple
        value={values.to}
        onChange={(value) => set("to", value)}
        error={missingRecipient ? "Add at least one recipient" : errors.to}
        hint={
          draft.to.length === 0
            ? "No contact of this client receives invoices yet - tick it on the client's Contacts tab"
            : "Separate addresses with commas"
        }
      />
      <TextField
        id={`${id}-cc`}
        label="Cc"
        type="email"
        multiple
        value={values.cc}
        onChange={(value) => set("cc", value)}
        error={errors.cc}
      />
      <TextField
        id={`${id}-subject`}
        label="Subject"
        value={values.subject}
        onChange={(value) => set("subject", value)}
        error={errors.subject}
      />
      <TextAreaField
        id={`${id}-body`}
        label="Message"
        value={values.body}
        onChange={(value) => set("body", value)}
        error={errors.body}
        rows={12}
      />

      {manual && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={onDownload} disabled={downloading}>
            <Download className="h-4 w-4" aria-hidden />
            {downloading ? "Downloading…" : "Download PDF"}
          </Button>
          <a
            href={gmailComposeUrl(message, mailbox)}
            target="_blank"
            rel="noopener noreferrer"
            className={buttonClass("outline")}
          >
            <ExternalLink className="h-4 w-4" aria-hidden />
            Open in Gmail
          </a>
          <a href={mailtoUrl(message)} className={buttonClass("outline")}>
            <ExternalLink className="h-4 w-4" aria-hidden />
            Open in mail app
          </a>
          <Button variant="ghost" onClick={() => void copyText()}>
            <Copy className="h-4 w-4" aria-hidden />
            {copied ? "Copied" : "Copy subject and message"}
          </Button>
        </div>
      )}
      {manual && alreadySent && (
        <Alert>This invoice is already recorded as sent, so e-mailing it again is not recorded.</Alert>
      )}

      <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:items-center sm:justify-end">
        {draft.email_configured && (
          <Button variant="ghost" className="sm:mr-auto" onClick={() => setManual(!manual)}>
            {manual ? "Send from the app instead" : `Send it myself from ${mailbox}`}
          </Button>
        )}
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          {manual && alreadySent ? "Close" : "Cancel"}
        </Button>
        {manual ? (
          !alreadySent && (
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : "Mark as e-mailed"}
            </Button>
          )
        ) : (
          <Button type="submit" disabled={saving}>
            {saving ? "Sending…" : "Send e-mail"}
          </Button>
        )}
      </div>
    </form>
  );
}
